import { useEffect, useState } from "react";
import { gsap } from "gsap";
import { Link, useSearchParams } from "react-router-dom";
import ContactModal from "../ContactModal";
import EstimateModal from "../EstimateModal";
import CaseStudyModal from "../CaseStudyModal";
import MobileNavMenu from "./MobileNavMenu";
import { ArrowRight } from "lucide-react";
import { logo, logoB } from "../../constants";

export interface CardNavProps {
  className?: string;
  initialColor?: string;
}

const CardNav: React.FC<CardNavProps> = ({
  className = "",
  initialColor = "[#f5f5f5]",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);

  const dropdownItems = [
    { name: "Web Services", href: "/web-services" },
    { name: "Brand Growth Solution", href: "/brandgrowthsolution" },
    { name: "Smart Tech Solution", href: "/smarttechsolution" },
    { name: "WhatsApp & SMS Marketing", href: "/whatsappsmsmarketing" },
  ];

  // modalConfig
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [modalType, setModalType] = useState<string | null>(null);
  const [csIdType, setCsIdType] = useState<string | null>(null);

  useEffect(() => {
    const type = searchParams.get("form");
    const id = searchParams.get("id");
    if (type) {
      openModal(type);
    } else if (id) {
      openGalleryModal(id);
    } else {
      setModalType(null);
      setCsIdType(null);
    }
  }, [searchParams]);

  const openModal = (type: string) => {
    setModalType(type);
    setIsModalOpen(true);
  };
  const openGalleryModal = (type: string) => {
    setCsIdType(type);
    setIsGalleryOpen(true);
  };

  const closeModal = () => {
    setTimeout(() => {
      setSearchParams({});
      setModalType(null);
      setCsIdType(null);
    }, 700);
  };
  // modalconfig end

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    if (isExpanded) setIsExpanded(false);
    else setIsExpanded(true);
  };

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isExpanded) toggleMenu();

    const rawTarget = e.currentTarget.getAttribute("to");
    if (!rawTarget) return;

    try {
      // Works for "#hero" or "/#hero"
      const url = new URL(rawTarget, window.location.origin);
      const { pathname, hash } = url;

      // ✅ Only intercept if it's the same page & has a hash
      if (hash && pathname === window.location.pathname) {
        e.preventDefault();
        const element = document.querySelector(hash);
        if (element) {
          try {
            gsap.to(window, {
              delay: 1.2,
              duration: 2,
              scrollTo: element,
              ease: "power2.out",
              overwrite: "auto",
            });
          } catch {
            setTimeout(() => {
              element.scrollIntoView({ behavior: "smooth" });
            }, 1200);
          }
        }
      }
      // Else: let React Router handle navigation as usual
    } catch (err) {
      console.error("Invalid link target:", rawTarget, err);
    }
  };

  return (
    <>
      <div
        className={`absolute left-1/2 -translate-x-1/2 w-[93vw] md:max-w-[1432px] z-[99] top-[0.6em] md:top-[1rem] ${className}`}
      >
        <nav className="bg-transparent block h-[70px] p-0 relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-[70px] flex items-center justify-between py-2 px-4 md:px-10 z-[2]">
            {/* Logo */}
            <div className="logo-container flex items-center order-1">
              <div
                className={`lg:hover:translate-x-2 active:translate-x-2 ${
                  isExpanded
                    ? "text-[#0d1821] lg:hover:text-[#00a878]  active:text-[#00a878]"
                    : `${
                        isScrolled
                          ? "opacity-0"
                          : `opacity-100 text-${initialColor} lg:hover:text-[#00a878]  active:text-[#00a878]`
                      }`
                } transition-all duration-300 ease-in`}
              >
                <Link to="/#hero" onClick={handleSmoothScroll}>
                  <img
                    src={`${isExpanded ? logoB : `${initialColor === "[#f5f5f5]" ? logo : logoB}`}`}
                    alt="Adszie Logo"
                    className="w-28 md:w-36 lg:w-40 pb-1 object-contain lg:pb-0"
                  />
                </Link>
              </div>
            </div>

            {/* Contact Button */}
            <Link
              type="button"
              className={`group inline-flex items-center justify-center shadow-md border-0 rounded-full pl-3 pr-2 md:px-4 h-11 md:h-[12] font-medium cursor-pointer transition-all duration-1200 ${
                isExpanded
                  ? "bg-[#0d1821] text-[#f5f5f5] md:hover:bg-[#00a878]"
                  : "bg-[#f5f5f5] text-[#0d1821] md:hover:bg-[#c3f620]"
              } order-2 ml-auto mr-4 md:mr-10 transition-[background-color,color] [transition-duration:500ms,500ms] 
                [transition-timing-function:ease-in,ease-in]`}
              to={"?form=contact"}
              onClick={handleSmoothScroll}
            >
              Contact Us
              <ArrowRight
                className="ml-1 w-5 h-5 rotate-45 md:rotate-0  md:group-hover:rotate-45 md:group-hover:-translate-y-[2px] md:group-hover:scale-125 md:transition-[transform,color] md:[transition-duration:200ms,500ms] 
                md:[transition-timing-function:ease-in,ease-in] md:[transform-origin:40%_50%]"
              />
            </Link>

            {/* Hamburger Button */}
            <div
              className={`group h-11 w-11 md:h-12 md:w-12 flex flex-col items-center justify-center shadow-md rounded-full border-2 ${
                isExpanded
                  ? "border-[#0d1821] bg-[#f5f5f5]"
                  : `${
                      isScrolled
                        ? "bg-[#f5f5f5] border-[#f5f5f5]"
                        : "border-[#f5f5f5] bg-transparent"
                    }`
              } md:hover:border-[#00a878] cursor-pointer gap-[6px] order-3 transition-[border-color,background-color] [transition-duration:500ms,500ms] [transition-timing-function:ease-in,ease-in]`}
              onClick={toggleMenu}
              role="button"
              aria-label={isExpanded ? "Close menu" : "Open menu"}
              tabIndex={0}
            >
              <div
                className={`hamburger-line w-[30px] h-[3px] rounded-[300px] transition-[background-color,transform] [transition-duration:500ms,300ms] 
                [transition-timing-function:ease-in,ease-out] [transform-origin:50%_50%] ${
                  isExpanded
                    ? "translate-y-[5px] rotate-45 bg-[#0d1821]"
                    : `${isScrolled ? "bg-[#0d1821]" : `bg-${initialColor}`}`
                } md:group-hover:bg-[#00a878]`}
              />
              <div
                className={`hamburger-line w-[30px] h-[3px] rounded-[300px] transition-[background-color,transform] [transition-duration:500ms,300ms] [transition-timing-function:ease-in,ease-out] [transform-origin:50%_50%] ${
                  isExpanded
                    ? "-translate-y-[4px] -rotate-45 bg-[#0d1821]"
                    : `${isScrolled ? "bg-[#0d1821]" : `bg-${initialColor}`}`
                } md:group-hover:bg-[#00a878]`}
              />
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      <MobileNavMenu isOpen={isExpanded}>
        <Link
          to="/#hero"
          className="hover:text-[#00a878] text-4xl md:text-6xl transition-[color] [transition-duration:300ms] [transition-timing-function:ease-in] mb-8"
          onClick={handleSmoothScroll}
        >
          Home
        </Link>
        <button
          className="hover:text-[#00a878] pl-10 md:pl-12 text-4xl md:text-6xl inline-flex items-center group transition-[color] [transition-duration:300ms] [transition-timing-function:ease-in] mb-8"
          onClick={() => setIsServicesOpen(!isServicesOpen)}
        >
          Services
          <ArrowRight className="ml-1 w-11 h-11 md:w-16 md:h-16 rotate-45 md:rotate-0  md:group-hover:rotate-45 md:group-hover:-translate-y-[2px] md:group-hover:scale-125 md:transition-[transform,color] md:[transition-duration:200ms,300ms] md:[transition-timing-function:ease-in,ease-in] md:[transform-origin:40%_50%]" />
        </button>
        <div
          className={`overflow-hidden transition-all duration-500 ease-in-out ${
            isServicesOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="space-y-2 text-center mb-8">
            {dropdownItems.map((drop) => (
              <Link
                key={drop.name}
                to={drop.href}
                onClick={handleSmoothScroll}
                className="block hover:text-[#00a878] font-poppins text-xl md:text-3xl transition-[color] [transition-duration:300ms] [transition-timing-function:ease-in]"
              >
                {drop.name}
              </Link>
            ))}
          </div>
        </div>

        <Link
          to="/#works"
          className="hover:text-[#00a878] text-4xl md:text-6xl transition-[color] [transition-duration:300ms] [transition-timing-function:ease-in] mb-8"
          onClick={handleSmoothScroll}
        >
          Our Works
        </Link>
        <Link
          type="button"
          to={"?form=estimate"}
          className="hover:text-[#00a878] text-4xl md:text-6xl transition-[color] [transition-duration:300ms] [transition-timing-function:ease-in] mb-8"
          onClick={handleSmoothScroll}
        >
          Estimate
        </Link>
        <Link
          to="/portfolio"
          className="hover:text-[#00a878] text-4xl md:text-6xl transition-[color] [transition-duration:300ms] [transition-timing-function:ease-in] mb-8"
          onClick={handleSmoothScroll}
        >
          Portfolio
        </Link>
        <Link
          to="/#whatwedo"
          className="hover:text-[#00a878] text-4xl md:text-6xl transition-[color] [transition-duration:300ms] [transition-timing-function:ease-in]"
          onClick={handleSmoothScroll}
        >
          About Us
        </Link>
      </MobileNavMenu>

      {/* modals */}
      {modalType === "contact" && (
        <ContactModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            closeModal();
          }}
        />
      )}
      {modalType === "estimate" && (
        <EstimateModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            closeModal();
          }}
        />
      )}
      {csIdType && (
        <CaseStudyModal
          projectId={Number(csIdType)}
          isOpen={isGalleryOpen}
          onClose={() => {
            setIsGalleryOpen(false);
            closeModal();
          }}
        />
      )}
    </>
  );
};

export default CardNav;
